import { cn } from "@/lib/utils"
import { AcademicianButton } from "@/components/academician/academician-button"
import { PlayIcon, ChevronRightIcon } from "lucide-react"
import { Progress } from "@/components/ui/progress"

interface CurrentModuleCardProps {
  moduleName?: string
  progress?: number
  currentChapter?: string | null
  nextChapter?: string | null
  continueHref?: string
  onContinue?: () => void
  className?: string
}

export function CurrentModuleCard({
  moduleName = "No module assigned",
  progress = 0,
  currentChapter = "Not started",
  nextChapter,
  continueHref = "/founder/modules",
  onContinue,
  className,
}: CurrentModuleCardProps) {
  return (
    <div
      className={cn(
        "bg-card border border-border rounded-lg p-6 space-y-4",
        className
      )}
    >
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Current Module
        </h3>
        <p className="text-2xl font-bold text-foreground">{moduleName}</p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-semibold text-foreground">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Current Chapter */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {currentChapter ? "Current Chapter" : "Status"}
        </div>
        <div className="text-sm font-medium text-foreground">
          {currentChapter || "All content completed"}
        </div>
      </div>

      {/* Continue Button */}
      <a href={continueHref} className="block">
        <AcademicianButton
          variant="primary"
          size="lg"
          leftIcon={<PlayIcon />}
          className="w-full"
          onClick={onContinue}
        >
          Continue Learning
        </AcademicianButton>
      </a>

      {/* Next Chapter Preview */}
      {nextChapter && (
        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Up Next</div>
              <div className="font-medium text-foreground">{nextChapter}</div>
            </div>
            <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      )}
    </div>
  )
}
